import {
  ArrowClockwiseIcon,
  ArrowCounterClockwiseIcon,
  EraserIcon,
  FloppyDiskBackIcon,
  LineSegmentIcon,
  MinusIcon,
  PaletteIcon,
  PencilSimpleIcon,
  PlusIcon,
  RectangleDashedIcon,
  TrashSimpleIcon,
  XIcon,
  PaintBucketIcon,
  HighlighterIcon,
  FilePlusIcon,
  CrosshairIcon,
  CopyIcon,
  HandPalmIcon,
  CameraPlusIcon,
} from "@phosphor-icons/react";
import {
  Box,
  Button,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
} from "@mui/material";
import { pdfjs } from "react-pdf";
import { useEffect, useRef, useState } from "react";
import { StyledFileEditor, StyledSlider } from "./fileEditor.styled";
import { File } from "../../models/File";
import { PageLoader } from "./pageLoader";
import { usePanZoom } from "../../hooks/fileEditor/usePanZoom";
import { useHistory } from "../../hooks/fileEditor/useHistory";
import { useDrawing } from "../../hooks/fileEditor/useDrawing";
import { useProject } from "../../stores/useProject";
import { useProfile } from "../../stores/useProfile";
import { useFile } from "../../hooks/fileEditor/useFile";
import { useAsyncStatus } from "../../hooks/useAsyncStatus";
import { AddImage } from "../addImage/AddImage";
import { useAddImage } from "../../hooks/fileEditor/useAddImage";

pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.min.js`;

type FileEditorProps = {
  opened: boolean;
  file: File | null;
  onClose: () => void;
};

export type Tool =
  | "pen"
  | "hand"
  | "highlight-rect"
  | "eraser"
  | "line"
  | "rectangle"
  | "colorPicker"
  | "undo"
  | "redo"
  | "delete"
  | "fill";

type Color = { color: string; hex: string };
const Colors: Color[] = [
  { color: "red", hex: "#7C0902" },
  { color: "pink", hex: "#FB607F" },
  { color: "magenta", hex: "#AB274F" },
  { color: "orange", hex: "#C46210" },
  { color: "gold", hex: "#E09540" },
  { color: "yellow", hex: "#FFBF00" },
  { color: "lime", hex: "#8DB600" },
  { color: "green", hex: "#568203" },
  { color: "darkgreen", hex: "#013220" },
  { color: "darkteal", hex: "#004953" },
  { color: "teal", hex: "#317873" },
  { color: "cyan", hex: "#399D9B" },
  { color: "blue", hex: "#007FFF" },
  { color: "darkblue", hex: "#003262" },
  { color: "purple", hex: "#4B0082" },
  { color: "charcoal", hex: "#1B1B1B" },
];

export const FileEditor = ({ opened, file, onClose }: FileEditorProps) => {
  const canvasRefs = useRef<Record<number, HTMLCanvasElement | null>>({});
  const previewCanvasRefs = useRef<Record<number, HTMLCanvasElement | null>>(
    {},
  );
  const viewportRef = useRef<HTMLDivElement | null>(null);
  const [tool, setTool] = useState<Tool>("hand");
  const [size, setSize] = useState(3);
  const [color, setColor] = useState<Color>(Colors.at(-1)!);
  const [activePage, setActivePage] = useState(0);
  const [extraPages, setExtraPages] = useState(0);
  const [toggleColorPicker, setToggleColorPicker] = useState<boolean>(false);
  const [removedPages, setRemovedPages] = useState<Set<number>>(new Set());
  const [fileName, setFileName] = useState<string>(file?.filename ?? "");
  const [changeName, setChangeName] = useState<boolean>(false);
  const [viewFile, setViewFile] = useState<File>(file!);
  const [cameraOpened, setCameraOpened] = useState<boolean>(false);
  const [basePageCount, setBasePageCount] = useState<number>(0);
  const [pendingCapturedImage, setPendingCapturedImage] = useState<{
    pageIndex: number;
    dataUrl: string;
  } | null>(null);

  const toolRef = useRef(tool);
  const sizeRef = useRef(size);
  const colorRef = useRef(color);

  const { updateFile } = useProject();
  const { user } = useProfile();
  const { saveEditedPdf, ensurePdfBase64 } = useFile();
  const { run, waitingNode } = useAsyncStatus();
  const panZoom = usePanZoom(viewportRef, tool);
  const { clearPage, pushState, redo, undo } = useHistory(canvasRefs);
  const { queueAddImagePage, onCanvasReady } = useAddImage({
    basePageCount,
    extraPages,
    setExtraPages,
    setActivePage,
    pushState,
    canvasRefs,
  });

  useEffect(() => {
    setToggleColorPicker(false);
    toolRef.current = tool;
  }, [tool]);
  useEffect(() => {
    sizeRef.current = size;
  }, [size]);
  useEffect(() => {
    colorRef.current = color;
    setToggleColorPicker(false);
  }, [color]);

  useEffect(() => {
    if (!opened) return;

    const prevent = (e: TouchEvent) => {
      if (e.touches.length > 1) {
        e.preventDefault();
      }
    };

    document.addEventListener("touchmove", prevent, { passive: false });

    return () => {
      document.removeEventListener("touchmove", prevent);
    };
  }, [opened]);

  useEffect(() => {
    if (!opened || !file) return;

    (async () => {
      const pdfBase64 = await ensurePdfBase64(file);

      setViewFile({
        ...file,
        base64: pdfBase64,
        mimeType: "application/pdf",
        filename: file.filename.replace(/\.(png|jpg|jpeg)$/i, ".pdf"),
      });
    })().catch(console.error);
  }, [opened, file]);

  const drawing = useDrawing({
    canvasRefs,
    previewCanvasRefs,
    toolRef,
    sizeRef,
    colorRef,
    zoom: panZoom.zoom,
    pan: panZoom.pan,
    pushState: pushState,
  });

  const iconColor =
    color.color !== "charcoal" ? color : { color: "white", hex: "#EEF0F2" };

  const handleSave = async () => {
    if (!user || !viewFile) return;
    setChangeName(false);

    const editedBase64 = await saveEditedPdf({
      file: viewFile,
      canvasRefs,
      extraPages,
      removedPages,
    });

    const response = await run(() =>
      updateFile(user, viewFile.id, {
        ...viewFile,
        fileName: fileName,
        base64: editedBase64,
      }),
    );

    onClose();
  };

  const toggleRemovePage = (pageIndex: number) => {
    setRemovedPages((prev) => {
      const next = new Set(prev);
      if (next.has(pageIndex)) next.delete(pageIndex);
      else next.add(pageIndex);
      return next;
    });
    canvasRefs.current[pageIndex] = null;
    previewCanvasRefs.current[pageIndex] = null;
  };

  const handleCapture = (imageStr: string) => {
    setCameraOpened(false);
    queueAddImagePage(imageStr);
    const img = document.createElement("img");
    img.src = imageStr;
    img.style.maxWidth = "100%";
    img.style.border = "2px solid red";
    document.body.appendChild(img);
  };

  if (!opened || !file) return null;

  return (
    <StyledFileEditor>
      {waitingNode}

      <AddImage
        opened={cameraOpened}
        onClose={() => setCameraOpened(false)}
        onCapture={handleCapture}
      />

      <Box className="contentWrapper">
        <Box
          ref={viewportRef}
          className="documentWrapper viewport"
          onPointerDown={panZoom.onPointerDown}
          onPointerMove={panZoom.onPointerMove}
          onPointerUp={panZoom.onPointerUp}
          onPointerCancel={panZoom.onPointerUp}
          style={{ touchAction: tool === "hand" ? "none" : "auto" }}>
          <Box
            className="panZoomLayer"
            style={{
              touchAction: tool === "hand" ? "" : "none",
              transform: `translate(${panZoom.pan.x}px, ${panZoom.pan.y}px) scale(${panZoom.zoom})`,
            }}>
            <PageLoader
              file={viewFile}
              canvasRefs={canvasRefs}
              previewCanvasRefs={previewCanvasRefs}
              removedPages={removedPages}
              onPointerDown={(e) => {
                const page = Number(e.currentTarget.dataset.page);
                setActivePage(page);

                drawing.onPointerDown(e);
              }}
              onPointerMove={(e) => {
                drawing.onPointerMove(e);
              }}
              onPointerUp={(e) => {
                drawing.onPointerUp(e);
              }}
              extraPages={extraPages}
              onPageRemove={(index) => toggleRemovePage(index)}
              onLoad={(num) => setBasePageCount(num)}
              onCanvasReady={onCanvasReady}
            />
          </Box>
        </Box>

        {changeName && (
          <Box className="saveWindow">
            <TextField
              value={fileName}
              className="input"
              variant="outlined"
              fullWidth
              onChange={(e) => setFileName(e.target.value)}
            />
            <Box className="buttonWrapper">
              <Button onClick={() => setChangeName(false)}>Sluiten</Button>
              <Button onClick={handleSave}>Opslaan</Button>
            </Box>
          </Box>
        )}

        <Box className="toolbar">
          <ToggleButtonGroup
            exclusive
            className="buttonGroup"
            value={tool}
            onChange={(_, v) => v && setTool(v)}>
            <Button
              value="reset"
              className="button"
              onClick={() => panZoom.reset()}>
              <CrosshairIcon size={26} weight="bold" />
            </Button>
            <ToggleButton value="hand" className="button">
              <HandPalmIcon
                size={26}
                weight="bold"
                color={tool === "hand" ? iconColor.hex : color.hex}
              />
            </ToggleButton>
            <ToggleButton value="pen" className="button">
              <PencilSimpleIcon
                size={26}
                weight="bold"
                color={tool === "pen" ? iconColor.hex : color.hex}
              />
            </ToggleButton>
            <ToggleButton value="highlight-rect" className="button">
              <HighlighterIcon
                size={26}
                weight="bold"
                color={tool === "highlight-rect" ? iconColor.hex : color.hex}
              />
            </ToggleButton>
            <ToggleButton value="line" className="button">
              <LineSegmentIcon
                size={26}
                weight="bold"
                color={tool === "line" ? iconColor.hex : color.hex}
              />
            </ToggleButton>
            <ToggleButton value="rectangle" className="button">
              <RectangleDashedIcon
                size={26}
                weight="bold"
                color={tool === "rectangle" ? iconColor.hex : color.hex}
              />
            </ToggleButton>
            <ToggleButton value="fill" className="button">
              <PaintBucketIcon
                size={26}
                weight="bold"
                color={tool === "fill" ? iconColor.hex : color.hex}
              />
            </ToggleButton>
            <ToggleButton value="eraser" className="button">
              <EraserIcon size={26} weight="bold" />
            </ToggleButton>
            <Button
              value="colorPicker"
              className="button"
              onClick={() => setToggleColorPicker(true)}>
              <PaletteIcon
                size={26}
                weight="bold"
                color={tool === "colorPicker" ? iconColor.hex : color.hex}
              />
            </Button>
            <Button
              value="undo"
              className="button"
              onClick={() => undo(activePage)}>
              <ArrowCounterClockwiseIcon size={26} weight="bold" />
            </Button>
            <Button
              value="redo"
              className="button"
              onClick={() => redo(activePage)}>
              <ArrowClockwiseIcon size={26} weight="bold" />
            </Button>
            <Button
              value="addpage"
              className="button"
              onClick={() => setExtraPages((p) => p + 1)}>
              <FilePlusIcon size={26} weight="bold" />
            </Button>
            <Button
              value="addImage"
              className="button"
              onClick={() => setCameraOpened(true)}>
              <CameraPlusIcon size={26} weight="bold" />
            </Button>

            <Button
              value="delete"
              className="button"
              onClick={() => clearPage(activePage)}>
              <TrashSimpleIcon size={26} weight="bold" />
            </Button>
            <Button value="saveAsCopy" className="button" onClick={handleSave}>
              <CopyIcon size={26} weight="bold" />
            </Button>
            <Button
              value="save"
              className="button"
              onClick={() => setChangeName(true)}>
              <FloppyDiskBackIcon size={26} weight="bold" />
            </Button>
            <Button value="close" className="button" onClick={onClose}>
              <XIcon size={26} weight="bold" />
            </Button>
          </ToggleButtonGroup>

          <Box
            className={`slider ${
              (tool === "pen" && "toggled") ||
              (tool === "line" && "toggled") ||
              (tool === "rectangle" && "toggled") ||
              (tool === "eraser" && "toggled")
            }`}>
            <MinusIcon size={24} weight="bold" />
            <StyledSlider
              value={size}
              min={1}
              max={40}
              step={3}
              sx={{ color: color.hex }}
              onChange={(_, v) => setSize(v as number)}
              orientation="horizontal"
              marks={true}
            />
            <PlusIcon size={24} weight="bold" />
          </Box>

          <Box className={`colorpicker ${toggleColorPicker && "toggled"}`}>
            <ToggleButtonGroup
              exclusive
              className="buttonGroup color"
              value={color}
              onChange={(_, v) => v && setColor(v)}>
              {Colors.map((c) => (
                <ToggleButton key={c.color} value={c} className="button">
                  <PaletteIcon size={24} color={c.hex} weight="bold" />
                </ToggleButton>
              ))}
            </ToggleButtonGroup>
          </Box>
        </Box>
      </Box>
    </StyledFileEditor>
  );
};
